import { useState } from 'react'

export const HostileAcquisition = () => {
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<any>(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const [formData, setFormData] = useState({
    targetSymbol: 'NVDA',
    sector: 'technology',
    marketCap: 'large',
    analystCoverage: 'high'
  })

  const handleScan = async () => {
    setLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1800))
      
      const riskLevel = Math.random() > 0.5 ? 'high' : Math.random() > 0.3 ? 'medium' : 'low'
      const riskScore = riskLevel === 'high' ? Math.floor(Math.random() * 20) + 75 :
                       riskLevel === 'medium' ? Math.floor(Math.random() * 25) + 50 :
                       Math.floor(Math.random() * 30) + 20
      
      setResults({
        target: { symbol: formData.targetSymbol, name: `${formData.targetSymbol} Analysis` },
        riskLevel,
        riskScore,
        indicators: {
          unusual_trading: Math.floor(Math.random() * 100),
          insider_activity: Math.floor(Math.random() * 100), 
          media_coverage: Math.floor(Math.random() * 100),
          regulatory_filings: Math.floor(Math.random() * 100),
          market_rumors: Math.floor(Math.random() * 100)
        },
        vulnerabilities: [
          { 
            factor: 'Low Stock Price', 
            severity: Math.random() > 0.5 ? 'High' : 'Medium',
            description: 'Trading below intrinsic value makes target attractive'
          },
          { 
            factor: 'Management Conflicts', 
            severity: Math.random() > 0.6 ? 'High' : 'Medium',
            description: 'Internal disputes may weaken defense capabilities'
          },
          { 
            factor: 'Cash Position', 
            severity: Math.random() > 0.7 ? 'Low' : 'Medium',
            description: 'Limited cash reserves for defensive measures'
          },
          { 
            factor: 'Shareholder Concentration', 
            severity: Math.random() > 0.4 ? 'Medium' : 'High',
            description: 'Dispersed ownership structure increases vulnerability'
          }
        ],
        defenses: [
          'Poison pill strategy available',
          'Staggered board structure',
          'Significant insider ownership',
          'Strong financial performance'
        ],
        timeline: `${Math.floor(Math.random() * 30) + 30} days estimated takeover window`,
        confidence: Math.floor(Math.random() * 15) + 80
      })
    } catch (error) {
      console.error('Analysis failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-900">Hostile Acquisition Risk Scanner</h1>
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="w-6 h-6 bg-blue-100 hover:bg-blue-200 rounded-full flex items-center justify-center text-blue-600 text-sm font-medium transition-colors"
            >
              ?
            </button>
            {showTooltip && (
              <div className="absolute z-10 w-80 p-3 mt-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg">
                <div className="font-medium mb-1">What does this module do?</div>
                <div className="text-gray-300">
                  Analyzes companies for hostile takeover vulnerability by monitoring unusual trading patterns, insider activity, 
                  media coverage, and regulatory filings. Identifies structural weaknesses (low stock price, management conflicts) 
                  and defensive measures to predict acquisition risk levels and timeline estimates.
                </div>
                <div className="absolute top-0 left-6 -mt-1 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              </div>
            )}
          </div>
        </div>
        <p className="text-gray-600 mt-2">
          Identify potential takeover targets and assess acquisition vulnerability
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Target Analysis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Symbol
            </label>
            <input
              type="text"
              value={formData.targetSymbol}
              onChange={(e) => setFormData({...formData, targetSymbol: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sector
            </label>
            <select 
              value={formData.sector}
              onChange={(e) => setFormData({...formData, sector: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="technology">Technology</option>
              <option value="healthcare">Healthcare</option>
              <option value="financial">Financial</option>
              <option value="energy">Energy</option>
              <option value="consumer">Consumer</option>
              <option value="industrial">Industrial</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Market Cap
            </label>
            <select 
              value={formData.marketCap}
              onChange={(e) => setFormData({...formData, marketCap: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="small">Small Cap (&lt;$2B)</option>
              <option value="mid">Mid Cap ($2B-$10B)</option>
              <option value="large">Large Cap ($10B+)</option>
              <option value="mega">Mega Cap ($100B+)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Analyst Coverage
            </label>
            <select 
              value={formData.analystCoverage}
              onChange={(e) => setFormData({...formData, analystCoverage: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low Coverage</option>
              <option value="medium">Medium Coverage</option>
              <option value="high">High Coverage</option>
            </select>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleScan}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <span>🔍</span>
            {loading ? 'Scanning...' : 'Scan for Acquisition Risk'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Risk Overview */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className={`text-4xl font-bold ${
                  results.riskLevel === 'high' ? 'text-red-600' :
                  results.riskLevel === 'medium' ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {results.riskScore}
                </div>
                <div className="text-sm text-gray-600">Risk Score</div>
              </div>
              
              <div className="text-center">
                <div className={`text-xl font-bold capitalize ${
                  results.riskLevel === 'high' ? 'text-red-600' :
                  results.riskLevel === 'medium' ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {results.riskLevel}
                </div>
                <div className="text-sm text-gray-600">Risk Level</div>
              </div>
              
              <div className="text-center">
                <div className="text-xl font-bold text-blue-600">{results.confidence}%</div>
                <div className="text-sm text-gray-600">Confidence</div>
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">Timeline Estimate:</div>
              <div className="font-medium text-gray-900">{results.timeline}</div>
            </div>
          </div>

          {/* Risk Indicators */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Indicators</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(results.indicators).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className={`text-xl font-bold ${
                    (value as number) > 70 ? 'text-red-600' :
                    (value as number) > 40 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {value as number}%
                  </div>
                  <div className="text-xs text-gray-600 capitalize">
                    {key.replace(/_/g, ' ')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Vulnerabilities & Defenses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vulnerabilities</h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-gray-900">{vuln.factor}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        vuln.severity === 'High' ? 'bg-red-100 text-red-800' :
                        vuln.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {vuln.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{vuln.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Defensive Measures</h3>
              <div className="space-y-2">
                {results.defenses.map((defense: string, index: number) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-green-500">🛡️</span>
                    <span className="text-sm text-gray-700">{defense}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}